import React, { Suspense } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useSelector } from "react-redux";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

const QRCode = React.lazy(() =>
  import("qrcode.react").then((module) => ({
    default: module.QRCodeSVG,
  })),
);

const labelStyle = {
  fontSize: 12,
  color: "#6b7a8a",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 4,
};

const valueStyle = {
  fontSize: 16,
  color: "#1a2330",
  fontWeight: 500,
};

const rowStyle = {
  padding: 16,
  borderBottom: "1px solid #e6ecf2",
  textAlign: "left",
};

const TurnDetail = ({
  turnDetail,
  doctor,
  turnType,
  description,
  cancelLink,
  direction,
  phone,
  dni,
  bookedAtUnix,
}) => {
  const documentNumber = dni || turnDetail?.dni;
  const auditUnix = bookedAtUnix || turnDetail?.bookedAtUnix;
  const { tenant, tenantTimezone, organization, tenantLogo } = useSelector(
    (store) => store.tenantData,
  );

  const hourLabel = turnDetail?.hour
    ? dayjs(turnDetail.hour)
        .tz(tenantTimezone || "America/Buenos_Aires")
        .locale("es")
        .format("dddd DD [de] MMMM [a las] HH:mm")
    : null;

  const professionalName = [doctor?.name, doctor?.lastname]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      style={{
        margin: 0,
        padding: "24px 12px",
        backgroundColor: "#f4f6f9",
        fontFamily:
          "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          backgroundColor: "#ffffff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,42,82,0.08)",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg,#002a52 0%,#0a4a8a 100%)",
            padding: "28px 24px",
            textAlign: "center",
          }}
        >
          <img
            src={`https://storage.googleapis.com/bs-proyecto1.appspot.com/${tenant}/${
              tenantLogo || "logo"
            }.png`}
            alt="Logo"
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              objectFit: "contain",
              backgroundColor: "#ffffff",
              padding: 6,
              display: "block",
              margin: "0 auto 12px",
            }}
          />
          <div
            style={{
              display: "inline-block",
              backgroundColor: "rgba(255,255,255,0.18)",
              color: "#ffffff",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 1,
              textTransform: "uppercase",
              padding: "6px 14px",
              borderRadius: 999,
              marginBottom: 10,
            }}
          >
            ✓ Confirmado
          </div>
          <h1
            style={{
              margin: 0,
              color: "#ffffff",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            Tu turno en {organization}
          </h1>
        </div>

        <div style={{ padding: "28px 24px 8px" }}>
          <h2
            style={{
              margin: "0 0 16px",
              color: "#002a52",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            🗓️ Detalles del turno
          </h2>

          <div
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: 12,
              border: "1px solid #e6ecf2",
              overflow: "hidden",
            }}
          >
            <div style={rowStyle}>
              <div style={labelStyle}>📅 Fecha y hora</div>
              <div style={{ ...valueStyle, fontWeight: 600 }}>
                {hourLabel ? `${hourLabel} hs` : ""}
              </div>
            </div>

            {documentNumber ? (
              <div style={rowStyle}>
                <div style={labelStyle}>🪪 DNI / CUIL</div>
                <div style={valueStyle}>{documentNumber}</div>
              </div>
            ) : null}

            <div style={rowStyle}>
              <div style={labelStyle}>📍 Dirección</div>
              <div style={valueStyle}>{direction || ""}</div>
            </div>

            <div style={{ ...rowStyle, borderBottom: "none" }}>
              <div style={labelStyle}>👨🏽‍⚕️ Profesional</div>
              <div style={valueStyle}>{professionalName}</div>
            </div>
          </div>

          {description ? (
            <div
              style={{
                marginTop: 16,
                backgroundColor: "#fff8e6",
                borderLeft: "4px solid #f5a623",
                borderRadius: 8,
                padding: "14px 16px",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  color: "#8a5a00",
                  fontSize: 14,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                ⚠️ Importante
              </div>
              <div
                style={{
                  color: "#5a4a20",
                  fontSize: 14,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {description}
              </div>
            </div>
          ) : null}

          {turnDetail?.services?.length > 0 ? (
            <div style={{ marginTop: 16, textAlign: "left" }}>
              <h3
                style={{
                  margin: "0 0 10px",
                  color: "#002a52",
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                🩺 Servicios
              </h3>
              {turnDetail.services.map((service) => (
                <div
                  key={service._id || service.name}
                  style={{
                    padding: "12px 14px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e6ecf2",
                    borderRadius: 10,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 15,
                        color: "#1a2330",
                        fontWeight: 600,
                      }}
                    >
                      {service.name}
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        color: "#002a52",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {service?.price?.toLocaleString("es-AR", {
                        style: "currency",
                        currency: "ARS",
                        minimumFractionDigits: 2,
                      }) || ""}
                    </div>
                  </div>
                  {service?.description ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7a8a",
                        marginTop: 4,
                        lineHeight: 1.4,
                      }}
                    >
                      {service.description}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {cancelLink ? (
          <div style={{ padding: "8px 24px 24px", textAlign: "center" }}>
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#6b7a8a" }}>
              ¿No vas a poder asistir?
            </p>
            <Suspense fallback={<div>Generando código QR...</div>}>
              <QRCode
                value={cancelLink}
                size={150}
                level="H"
                includeMargin={true}
              />
            </Suspense>
            <p
              style={{
                fontSize: 12,
                color: "#6b7a8a",
                margin: "10px 0 0",
                lineHeight: 1.4,
              }}
            >
              Escaneá el código para cancelar y dejar el espacio libre para otro
              paciente. También podés hacerlo desde el email (revisá el spam).
            </p>
          </div>
        ) : null}

        {phone ? (
          <div style={{ padding: "0 24px 24px", textAlign: "center" }}>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                backgroundColor: "#25D366",
                color: "#ffffff",
                textDecoration: "none",
                padding: "12px 22px",
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 999,
              }}
            >
              💬 Contactar: {phone}
            </a>
          </div>
        ) : null}

        <div
          style={{
            backgroundColor: "#f8fafc",
            padding: 24,
            textAlign: "center",
            borderTop: "1px solid #e6ecf2",
          }}
        >
          <img
            src="https://storage.googleapis.com/bs-proyecto1.appspot.com/logos/logo-square-dark-blue.png"
            alt="AmpinaSoft"
            width={120}
            style={{ display: "block", margin: "0 auto 10px", opacity: 0.85 }}
          />
          <p style={{ margin: "0 0 4px", fontSize: 13, color: "#6b7a8a" }}>
            Gracias por elegirnos 💙
          </p>
          <p style={{ margin: "0 0 8px", fontSize: 13, color: "#6b7a8a" }}>
            El equipo de AmpinaSoft
          </p>
          <a
            href="https://www.ampinasoft.com"
            style={{
              fontSize: 13,
              color: "#002a52",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            www.ampinasoft.com
          </a>
          {auditUnix ? (
            <div
              style={{
                marginTop: 10,
                fontSize: 9,
                color: "#bbb",
                letterSpacing: 0.3,
              }}
            >
              {auditUnix}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TurnDetail;
